/**
 * TEXTPRINT — Devis impression (Sprint 2 MVP).
 * Brief SIM-PLATFORM-TEXTIPRINT-2026-001 §C.
 *
 * MVP : liste + KPIs + filtres + actions workflow inline (envoyer / accepter /
 * refuser / convertir en commande). Création basique via dialog. Edition lignes
 * dialog viendra plus tard.
 */
import { useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, FileText, Send, CheckCircle2, XCircle, ArrowRightLeft } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useDevisImpression,
  useEnvoyerDevis, useAccepterDevis, useRefuserDevis, useConvertirDevis,
} from '@/hooks/useTextprintSprint2';
import {
  STATUT_DEVIS_LABELS,
  type DevisImpression, type StatutDevisImpression,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutDevisImpression, string> = {
  brouillon: 'ds-bd ds-bd-g',
  envoye:    'ds-bd ds-bd-info',
  accepte:   'ds-bd ds-bd-s',
  refuse:    'ds-bd ds-bd-d',
  expire:    'ds-bd ds-bd-warn',
  converti:  'ds-bd ds-bd-p',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const DevisImpressionPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statutFilter, setStatutFilter] = useState<StatutDevisImpression | ''>('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const queryParams = useMemo(() => ({
    limit: 500,
    statut: statutFilter || undefined,
    search: search.trim() || undefined,
  }), [statutFilter, search]);

  const { data, isLoading, refetch } = useDevisImpression(queryParams);
  const devis = data?.data ?? [];

  const envoyer   = useEnvoyerDevis();
  const accepter  = useAccepterDevis();
  const refuser   = useRefuserDevis();
  const convertir = useConvertirDevis();

  // KPIs
  const totalBrouillon = devis.filter((d) => d.statut === 'brouillon').length;
  const totalEnvoyes   = devis.filter((d) => d.statut === 'envoye').length;
  const totalAcceptes  = devis.filter((d) => d.statut === 'accepte').length;
  const totalConvertis = devis.filter((d) => d.statut === 'converti').length;
  const valeurTotaleCents = devis.reduce(
    (s, d) => s + Number(BigInt(d.totalTtcCents ?? '0')),
    0,
  );

  const wrapErr = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); } catch (err) { setError(extractError(err).message); }
  };

  const onConvertir = async (d: DevisImpression) => {
    if (!(await demander.confirmer({ titre: `Convertir le devis ${d.reference} en commande ferme ?`, message: 'Une CommandeImpression sera créée automatiquement avec les mêmes lignes.', confirmer: 'Convertir' }))) return;
    setError(null); setInfo(null);
    try {
      const result = await convertir.mutateAsync({ id: d.id });
      // result = { devis, commande } selon l'API. Affichage du numéro commande créé.
      const cmdRef = (result as { commande?: { reference: string } })?.commande?.reference;
      setInfo(cmdRef ? `✅ Commande ${cmdRef} créée depuis le devis ${d.reference}.` : `✅ Devis ${d.reference} converti.`);
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Devis Impression"
        subtitle={filiale ? `${devis.length} devis · ${filiale.fullName}` : 'Devis'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}
        {info && (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><CheckCircle2 size={18} /></span>
            <div className="ds-alert-txt">{info}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico"><FileText size={18} /></div>
            <div className="ds-kr-val">{devis.length}</div>
            <div className="ds-kr-lbl">Total devis</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico">📝</div>
            <div className="ds-kr-val">{totalBrouillon}</div>
            <div className="ds-kr-lbl">Brouillons</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico"><Send size={18} /></div>
            <div className="ds-kr-val">{totalEnvoyes}</div>
            <div className="ds-kr-lbl">Envoyés (en attente)</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico"><CheckCircle2 size={18} /></div>
            <div className="ds-kr-val">{totalAcceptes}</div>
            <div className="ds-kr-lbl">Acceptés (à convertir)</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico"><ArrowRightLeft size={18} /></div>
            <div className="ds-kr-val">{totalConvertis}</div>
            <div className="ds-kr-lbl">Convertis</div>
          </div>
        </div>

        <div className="ds-filters" style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Rechercher référence / client…"
            className="ds-fi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <select
            className="ds-fi"
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value as StatutDevisImpression | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_DEVIS_LABELS) as StatutDevisImpression[]).map((s) => (
              <option key={s} value={s}>{STATUT_DEVIS_LABELS[s]}</option>
            ))}
          </select>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--tx-3)', alignSelf: 'center' }}>
            💰 Valeur totale : <strong>{formatNumber(valeurTotaleCents / 100)} FCFA</strong>
          </div>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Devis Impression</div>
              <div className="ds-table-sub">
                {devis.length} devis — MVP Sprint 2 (création/édition complète bientôt)
              </div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Référence</th>
                <th style={{ width: 100 }}>Date</th>
                <th>Client</th>
                <th style={{ width: 130, textAlign: 'right' }}>Total TTC</th>
                <th style={{ width: 100 }}>Validité jusqu'à</th>
                <th style={{ width: 130 }}>Statut</th>
                <th style={{ width: 280 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              ) : devis.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun devis. Créez-en un via l'API ou la prochaine UI création (Sprint 2.5).</td></tr>
              ) : devis.map((d) => (
                <tr key={d.id}>
                  <td><code style={{ fontSize: 11, color: 'var(--tx-2)' }}>{d.reference}</code></td>
                  <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{formatDate(d.dateDevis)}</td>
                  <td style={{ fontSize: 13 }}>{d.client?.fullName ?? d.client?.companyName ?? <span style={{ color: 'var(--tx-3)' }}>—</span>}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                    {formatMontant(d.totalTtcCents ?? '0')}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{formatDate(d.dateExpiration)}</td>
                  <td>
                    <span className={STATUT_BADGE[d.statut]}>
                      {STATUT_DEVIS_LABELS[d.statut]}
                    </span>
                  </td>
                  <td>
                    {d.statut === 'brouillon' && (
                      <button className="ds-btn ds-btn-sm ds-btn-p" disabled={envoyer.isPending}
                        onClick={() => void wrapErr(() => envoyer.mutateAsync(d.id))}>
                        <Send size={11} /> Envoyer
                      </button>
                    )}
                    {d.statut === 'envoye' && (
                      <>
                        <button className="ds-btn ds-btn-sm ds-btn-s" disabled={accepter.isPending}
                          onClick={() => void wrapErr(() => accepter.mutateAsync(d.id))}
                          style={{ marginRight: 4 }}>
                          <CheckCircle2 size={11} /> Accepter
                        </button>
                        <button className="ds-btn ds-btn-sm ds-btn-d" disabled={refuser.isPending}
                          onClick={() => void wrapErr(() => refuser.mutateAsync(d.id))}>
                          <XCircle size={11} /> Refuser
                        </button>
                      </>
                    )}
                    {d.statut === 'accepte' && (
                      <button className="ds-btn ds-btn-sm ds-btn-p" disabled={convertir.isPending}
                        onClick={() => void onConvertir(d)}>
                        <ArrowRightLeft size={11} /> Convertir en commande
                      </button>
                    )}
                    {d.statut === 'converti' && d.commandeConvertie && (
                      <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                        → Commande <code>{d.commandeConvertie.reference}</code>
                      </span>
                    )}
                    {(d.statut === 'refuse' || d.statut === 'expire') && (
                      <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>Terminé</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
